import {pad} from "./utils";
import {DateTimeFormatter, Instant, LocalDateTime, nativeJs, ZoneId} from "@js-joda/core";
import "@js-joda/timezone";
import {ColumnSortParams} from "primereact/column";

const DEFAULT_FORMATTER = DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm");

export function durationSeconds(duration: number, durationUnit: string): number {
    switch (durationUnit) {
        case "SECONDS":
            return duration;
        case "MINUTES":
            return duration * 60;
        case "HOURS":
            return duration * 60 * 60;
        case "DAYS":
            return duration * 60 * 60 * 24;
        case "WEEKS":
            return duration * 60 * 60 * 24 * 7;
        case "MONTHS":
            return duration * 60 * 60 * 24 * 7 * 30;
        default:
            throw new Error("Not known time unit")
    }
}

export const dateTimeNow = (pattern: string) => formatDateTimeOrNow(pattern)


export function formatDateTimeOrNow(pattern: string, date?: Date | String | null) {
    if (date != null) {
        return formatDateTime(date, pattern)
    }

    return formatDateTime(new Date(), pattern)
}

export function formatDateTime(date: Date | String | null | undefined, pattern: string, defaultNone: string = "") {
    const tempDate = castToLocalDateTime(date)
    if (!tempDate) return defaultNone;

    return tempDate.format(DateTimeFormatter.ofPattern(pattern))
}

export function castToLocalDateTime(date: Date | String | null | undefined): LocalDateTime {
    if (date != null) {
        if (date instanceof Date) {
            return LocalDateTime.from(nativeJs(date))
        } else {
            return LocalDateTime.from(nativeJs(new Date(date as string)))
        }
    }

    return LocalDateTime.from(nativeJs(new Date()));
}

export function formatTime(date: Date | String | null | undefined) {
    let d: Date;
    if (date instanceof Date) {
        d = date;
    } else if (date instanceof String || typeof date === "string") {
        d = new Date(date as string);
    } else {
        return "";
    }

    return d.getFullYear() +
        '-' + pad(d.getMonth() + 1) +
        '-' + pad(d.getDate()) +
        ' ' + pad(d.getHours()) +
        ':' + pad(d.getMinutes()) +
        ':' + pad(d.getSeconds());
}

export function toDate(date: Date | String | string | undefined | null): Date | null {
    if (date) {
        if (date instanceof Date) return date;

        return new Date(date as string);
    } else {
        return null;
    }
}

export function formatDuration(secs: number) {
    let fmt = "";

    let sec_num = secs
    let days = Math.floor(sec_num / (3600 * 24));
    let hours = Math.floor((sec_num - (days * 3600 * 24)) / 3600);
    let minutes = Math.floor((sec_num - (days * 3600 * 24) - (hours * 3600)) / 60);
    let seconds = sec_num - (days * 3600 * 24) - (hours * 3600) - (minutes * 60);

    if (days > 0) {
        fmt += `${days}d `
    }
    if (hours > 0) {
        fmt += `${hours}h `
    }
    if (minutes > 0) {
        fmt += `${minutes}m `
    }
    if (seconds > 0) {
        fmt += `${seconds.toFixed(0)}s`
    }

    return fmt;
}

export function getDiffSeconds(date1: Date | String, date2: Date | String): number {
    let d1 = new Date(toDate(date1)!);
    let d2 = new Date(toDate(date2)!);

    return ((+d1) - (+d2)) / 1000;
}

function getMonday(date: Date) {
    let d = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    let day = d.getDay(),
        diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    return new Date(d.setDate(diff));
}

function getBeginningOfMonth(date: Date) {
    return new Date(date.getUTCFullYear(), date.getUTCMonth());
}

function getBeginningOfYear(date: Date) {
    return new Date(date.getUTCFullYear(), 0);
}

export const dateToReadableString = (date: Date | undefined, formatUntil?: ChronoUnitLegacy) => {
    let returnee = "";
    let tempDate = date ? date : new Date();

    returnee += tempDate.getFullYear();
    if (formatUntil === ChronoUnitLegacy.YEARS) {
        return returnee
    }

    returnee += "-" + ((tempDate.getMonth() + 1) >= 10 ? (tempDate.getMonth() + 1) : "0" + (tempDate.getMonth() + 1));
    if (formatUntil === ChronoUnitLegacy.MONTHS) {
        return returnee
    }

    returnee += "-" + (tempDate.getDate() >= 10 ? tempDate.getDate() : "0" + tempDate.getDate());
    if (formatUntil === ChronoUnitLegacy.DAYS) {
        return returnee
    }

    returnee += " " + (tempDate.getHours() >= 10 ? tempDate.getHours() : "0" + tempDate.getHours());
    if (formatUntil === ChronoUnitLegacy.HOURS) {
        return returnee
    }

    returnee += ":" + (tempDate.getMinutes() >= 10 ? tempDate.getMinutes() : "0" + tempDate.getMinutes());
    if (formatUntil === ChronoUnitLegacy.MINUTES) {
        return returnee
    }

    return returnee + ":" + (tempDate.getSeconds() >= 10 ? tempDate.getSeconds() : "0" + tempDate.getSeconds());
}

export function formatDatetimeDefault(datetime?: Instant | string | Date) {
    if(!datetime) return "";

    let instantObj: Instant;
    if (datetime instanceof Instant) {
        instantObj = datetime;
    } else if( typeof datetime === "string") {
        instantObj = Instant.parse(datetime as string);
    } else if( datetime instanceof Date) {
        instantObj = Instant.from(nativeJs(datetime));
    } else {
        throw new Error("Invalid instant type: " + datetime + " (" + typeof datetime + ")");
    }

    const localDateTime = instantObj.atZone(ZoneId.SYSTEM).toLocalDateTime();
    return localDateTime.format(DEFAULT_FORMATTER);
}


export enum ChronoUnitLegacy {
    SECONDS = 'SECONDS',
    MINUTES = 'MINUTES',
    HOURS = 'HOURS',
    DAYS = 'DAYS',
    WEEKS = 'WEEKS',
    MONTHS = 'MONTHS',
    YEARS = 'YEARS'
}

export enum ChronoUnitCompared {
    SECONDS = 1,
    MINUTES = 60,
    HOURS = MINUTES * 60,
    DAYS = HOURS * 24,
    WEEKS = DAYS * 7,
    MONTHS = DAYS * 30,
    YEARS = MONTHS * 12
}

export function truncateDate(fromDate: Date, truncateTo: ChronoUnitLegacy | String | undefined): Date {
    let date = new Date(fromDate);

    if (truncateTo == null) {
        return date;
    }

    date.setMilliseconds(0);
    if (truncateTo === ChronoUnitLegacy.SECONDS) return date;

    date.setSeconds(0);
    if (truncateTo === ChronoUnitLegacy.MINUTES) return date;

    date.setMinutes(0);
    if (truncateTo === ChronoUnitLegacy.HOURS) return date;

    date.setHours(0);
    if (truncateTo === ChronoUnitLegacy.DAYS) return date;

    date = getMonday(date);
    if (truncateTo === ChronoUnitLegacy.WEEKS) return date;

    date = getBeginningOfMonth(date);
    if (truncateTo === ChronoUnitLegacy.MONTHS) return date;

    date = getBeginningOfYear(date);
    if (truncateTo === ChronoUnitLegacy.YEARS) return date;

    return date;
}

export function addToDate(fromDate: Date, amount: number, unit: ChronoUnitLegacy | String | undefined): Date {
    let date = new Date(fromDate);

    if (amount == null || unit == null) {
        return date;
    }

    switch (unit) {
        case ChronoUnitLegacy.SECONDS:
            date.setSeconds(date.getSeconds() + amount);
            break;
        case ChronoUnitLegacy.MINUTES:
            date.setMinutes(date.getMinutes() + amount);
            break;
        case ChronoUnitLegacy.HOURS:
            date.setHours(date.getHours() + amount);
            break;
        case ChronoUnitLegacy.DAYS:
            date.setHours(date.getHours() + amount * 24);
            break;
        case ChronoUnitLegacy.WEEKS:
            date.setHours(date.getHours() + amount * 24 * 7);
            break;
        case ChronoUnitLegacy.MONTHS:
            date.setMonth(date.getMonth() + amount);
            break;
        case ChronoUnitLegacy.YEARS:
            date.setFullYear(date.getFullYear() + amount);
            break;
    }

    return date;
}

export function dateWithoutTimeZone(date: Date | undefined) {
    return addToDate(date || new Date(), (date || new Date()).getTimezoneOffset() * -1, ChronoUnitLegacy.MINUTES)
}

export function primereactDataTableSortDateFunction(e: ColumnSortParams) {
    if (e.data) {
        const orderMultiplier = e.order || 1;
        return [...e.data].sort((a, b) => {
            const valA = a.startsAt ? new Date(a.startsAt).getTime() : NaN;
            const valB = b.startsAt ? new Date(b.startsAt).getTime() : NaN;

            if (isNaN(valA) && !isNaN(valB)) {
                return -1 * orderMultiplier;
            } else if (!isNaN(valA) && isNaN(valB)) {
                return orderMultiplier;
            } else {
                return (valA - valB) * orderMultiplier;
            }
        });
    }
}


function chronoIsGreater(compared: ChronoUnitLegacy, comparedTo: ChronoUnitLegacy) {
    let comparedUnit = ChronoUnitCompared[compared];
    let comparedToUnit = ChronoUnitCompared[comparedTo];

    return comparedUnit.valueOf() > comparedToUnit.valueOf();
}


export namespace DateUtils {
    export const strFormatDateTime = (date: string, pattern: string, zoneId: string = "Europe/Vilnius") => {
        const ins = Instant.parse(date)
        return LocalDateTime.ofInstant(ins, ZoneId.of(zoneId)).format(DateTimeFormatter.ofPattern(pattern))
    }
}